import { IconButton } from '@mui/material'
import { FC } from 'react'
import { MAX_VOLUME } from '../constants'
import { normalizePlaybackSpeed, getRecordingError } from '../PlayerControls/playerControlsUtils'
import { useRRWebPlayerState } from './useRRWebPlayerState'
import { ValidatedRecording } from '../ertUtils'
import { VolumeControl } from '../PlayerControls/VolumeControl'
import { PlayerControlsBar } from '../PlayerControls/PlayerControlsBar'
import { SpeedControls } from '../PlayerControls/SpeedControls'

// Icon imports
import EngagementReviewMuteIcon from '../../../../assets/images/icons/engagement-review-mute-icon.svg?react'
import EngagementReviewSoundIcon from '../../../../assets/images/icons/engagement-review-sound-icon.svg?react'
import EngagementReviewSpecialistAudioMutedIcon from '../../../../assets/images/icons/engagement-review-specialist-audio-muted-icon.svg?react'
import EngagementReviewSpecialistAudioUnmutedIcon from '../../../../assets/images/icons/engagement-review-specialist-audio-unmuted-icon.svg?react'
import DownloadIcon from '../../../../assets/images/icons/download-icon.svg?react'

interface QAModePlayerControlsProps {
  /** Current time in seconds */
  currentTimeInSeconds?: number

  /** Total duration in seconds */
  durationInSeconds?: number

  /** Whether the player is currently playing */
  isPlaying?: boolean

  /**
   * The initial volume level. a number between 0 and 100.
   */
  initialVolumePercentage?: number

  /** Current playback speed multiplier */
  speed?: number

  /** Callback when play button is clicked */
  onPlay?: () => void

  /** Callback when pause button is clicked */
  onPause?: () => void

  /** Whether the controls are disabled */
  isBuffering?: boolean

  /**
   * Callback when the preferred volume level changes.
   *
   * The new volume level is provided as a parameter, expressed as a percentage.
   */
  onPlayerVolumePercentageChange?: (volumePercentage: number) => void

  /**
   * Callback when the preferred volume level changes.
   *
   * The new volume level is provided as a parameter, expressed as a percentage.
   */
  onSpecialistAudioVolumePercentageChange?: (volumePercentage: number) => void

  /** Callback when playback speed changes */
  onSpeedChange?: (speed: number) => void

  webRecordings?: ValidatedRecording[]
  audioRecordings?: ValidatedRecording[]

  engagementId: string
}

export const QAModePlayerControls: FC<QAModePlayerControlsProps> = ({
  currentTimeInSeconds,
  durationInSeconds,
  isPlaying = false,
  initialVolumePercentage = MAX_VOLUME / 100,
  speed = 1,
  onPlay,
  onPause,
  onPlayerVolumePercentageChange,
  onSpecialistAudioVolumePercentageChange,
  onSpeedChange,
  isBuffering = false,
  webRecordings,
  audioRecordings,
  engagementId
}) => {
  const currentSpeed = normalizePlaybackSpeed(speed)

  const handlePlayPause = () => {
    if (isPlaying) {
      onPause?.()
    } else {
      onPlay?.()
    }
  }

  const {
    playerMuted,
    playerVolume,
    handlePlayerVolumeChange,
    handleTogglePlayerMute,
    specialistAudioMuted,
    specialistAudioVolume,
    handleSpecialistAudioVolumeChange,
    handleToggleSpecialistAudioMute
  } = useRRWebPlayerState({ initialVolumePercentage, onPlayerVolumePercentageChange, onSpecialistAudioVolumePercentageChange })

  const webRecordingError = getRecordingError(webRecordings)
  const specialistAudioError = getRecordingError(audioRecordings)

  return (
    <PlayerControlsBar
      isPlaying={isPlaying}
      isBuffering={isBuffering}
      onPlayPause={handlePlayPause}
      errorMessage={webRecordingError}
      currentTimeInSeconds={currentTimeInSeconds ?? 0}
      durationInSeconds={durationInSeconds ?? 0}
      testIdPrefix="ert-player-controls"
    >
      <SpeedControls currentSpeed={currentSpeed} onSpeedChange={speed => onSpeedChange?.(speed)} />

      {/*Player Volume Control*/}
      <VolumeControl
        onClick={handleTogglePlayerMute}
        playerVolume={playerVolume}
        playerMuted={playerMuted}
        onChange={handlePlayerVolumeChange}
        icons={{
          mutedIcon: <EngagementReviewMuteIcon fontSize="small" />,
          unmutedIcon: <EngagementReviewSoundIcon fontSize="small" />
        }}
        testIds={{
          volumeSlider: 'ert-player-controls-volume-slider',
          muteButton: 'ert-player-controls-mute-button',
          unmuteButton: 'ert-player-controls-unmute-button'
        }}
      />

      {/*Specialist Audio Volume Control*/}
      <VolumeControl
        onClick={handleToggleSpecialistAudioMute}
        playerVolume={specialistAudioVolume}
        playerMuted={specialistAudioMuted}
        onChange={handleSpecialistAudioVolumeChange}
        errorMessage={specialistAudioError}
        icons={{
          mutedIcon: <EngagementReviewSpecialistAudioMutedIcon fontSize="small" />,
          unmutedIcon: <EngagementReviewSpecialistAudioUnmutedIcon fontSize="small" />
        }}
        testIds={{
          volumeSlider: 'ert-player-controls-specialist-volume-slider',
          muteButton: 'ert-player-controls-specialist-mute-button',
          unmuteButton: 'ert-player-controls-specialist-unmute-button'
        }}
      />

      {!!audioRecordings?.length && (
        <IconButton
          onClick={() => {
            for (let i = 0; i < audioRecordings.length; i++) {
              const audioRecording = audioRecordings[i]
              const url = URL.createObjectURL(
                new Blob(
                  audioRecording.segments.map(segment => segment.data),
                  { type: 'audio/webm' }
                )
              )

              const a = document.createElement('a')
              a.href = url
              a.download = `specialist_audio_${audioRecordings?.length > 1 ? `${i + 1}_` : ''}${engagementId}.opus`
              a.style.display = 'none'

              document.body.appendChild(a)
              a.click()

              document.body.removeChild(a)
              URL.revokeObjectURL(url)
            }
          }}
        >
          <DownloadIcon />
        </IconButton>
      )}
    </PlayerControlsBar>
  )
}
